export interface TrieNode {
  children: Map<string, TrieNode>
  isEnd: boolean
}

/**
 * Trie (árbol de prefijos) para almacenamiento y búsqueda eficiente de cadenas.
 * Inserción, búsqueda y eliminación en O(L), donde L es la longitud de la cadena.
 * Independiente del tamaño del diccionario; mucho más eficiente que un Set para prefijos.
 */
export class Trie {
  readonly root: TrieNode = { children: new Map(), isEnd: false }
  #size = 0

  /**
   * Inserta una palabra en el trie.
   * Si ya existe, no modifica el tamaño.
   */
  insert(word: string): void {
    let current = this.root
    for (const char of word) {
      if (!current.children.has(char)) {
        current.children.set(char, { children: new Map(), isEnd: false })
      }
      current = current.children.get(char)!
    }
    if (!current.isEnd) {
      current.isEnd = true
      this.#size++
    }
  }

  /**
   * Busca si la palabra exacta existe en el trie.
   */
  search(word: string): boolean {
    return this.#traverse(word)?.isEnd ?? false
  }

  /**
   * Comprueba si alguna palabra del trie comienza con el prefijo dado.
   */
  startsWith(prefix: string): boolean {
    return this.#traverse(prefix) !== null
  }

  /**
   * Elimina una palabra del trie.
   * Poda las ramas que queden completamente vacías.
   * @returns true si la palabra existía y fue eliminada, false si no existía.
   */
  delete(word: string): boolean {
    const deleted = this.#delete(this.root, word, 0)
    if (deleted) this.#size--
    return deleted
  }

  /**
   * Retorna todas las palabras del trie que comienzan con el prefijo dado.
   * Retorna array vacío si no hay coincidencias.
   */
  wordsWithPrefix(prefix: string): string[] {
    const node = this.#traverse(prefix)
    if (!node) return []
    const results: string[] = []
    this.#collect(node, prefix, results)
    return results
  }

  /**
   * Número de palabras distintas en el trie.
   */
  get size(): number {
    return this.#size
  }

  /**
   * Indica si el trie está vacío.
   */
  isEmpty(): boolean {
    return this.#size === 0
  }

  /**
   * Vacía el trie completamente.
   */
  clear(): void {
    this.root.children.clear()
    this.root.isEnd = false
    this.#size = 0
  }

  #traverse(str: string): TrieNode | null {
    let current = this.root
    for (const char of str) {
      if (!current.children.has(char)) return null
      current = current.children.get(char)!
    }
    return current
  }

  #delete(node: TrieNode, word: string, depth: number): boolean {
    if (depth === word.length) {
      if (!node.isEnd) return false
      node.isEnd = false
      return true
    }
    const char = word[depth]!
    const child = node.children.get(char)
    if (!child) return false
    const deleted = this.#delete(child, word, depth + 1)
    // Podar la rama si quedó vacía y no es fin de otra palabra
    if (deleted && !child.isEnd && child.children.size === 0) {
      node.children.delete(char)
    }
    return deleted
  }

  #collect(node: TrieNode, prefix: string, results: string[]): void {
    if (node.isEnd) results.push(prefix)
    for (const [char, child] of node.children) {
      this.#collect(child, prefix + char, results)
    }
  }
}
